import type { ApplicantOutcomeV2, ApplicantSectionV2 } from "@credit-acceptance/step6-bff-client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FieldPath, UseFormReturn } from "react-hook-form";

import type { SignersInput } from "./Signers.schema";
import { translatePersonViolations, type UnmappedViolation } from "./Signers.violations";

// ---------------------------------------------------------------------------
// useServerFieldErrors
//
// Applies the BFF's per-applicant Validations (see Signers.violations.ts) as
// RHF field errors, from two sources:
//   - GET/refetch: an effect keyed on the stringified content of
//     driver/secondSigner.validations, so a fresh dashboard load (or resync)
//     applies its violations immediately — not gated on isDirty, since these
//     errors are authoritative and there's no client-side re-validation to
//     race against.
//   - PUT/save: `applyFromSaveOutcome`, called synchronously from the save
//     orchestrator's onSaved callback, so a save's outcome is reflected the
//     same tick rather than waiting on the query-cache round-trip.
// Both sources share one "last applied paths" ref per leg, so whichever fires
// last simply wins (no accumulation of stale errors across cycles), and each
// application only clears/replaces the paths it itself previously set.
// ---------------------------------------------------------------------------

interface UseServerFieldErrorsArguments {
  form: UseFormReturn<SignersInput>;
  /** Driver section from the dashboard GET response (or the query-cache snapshot after a save). */
  driver?: ApplicantSectionV2;
  /** Second signer section from the dashboard GET response (or the query-cache snapshot after a save). */
  secondSigner?: ApplicantSectionV2;
  /** Currently rendered driver income-block count, for employment-slot bounds checking. */
  driverIncomeCount: number;
  /** Currently rendered 2nd-signer income-block count, for employment-slot bounds checking. */
  secondSignerIncomeCount: number;
  /** Currently rendered driver phone-row count, for phone-index bounds checking. */
  driverPhoneCount: number;
  /** Currently rendered 2nd-signer phone-row count, for phone-index bounds checking. */
  secondSignerPhoneCount: number;
}

/** A save response's per-leg outcome — undefined for a leg not part of that save request (left untouched). */
interface SaveOutcomeArguments {
  driver?: ApplicantOutcomeV2;
  secondSigner?: ApplicantOutcomeV2;
}

interface UseServerFieldErrorsResult {
  driverUnmapped: UnmappedViolation[];
  secondSignerUnmapped: UnmappedViolation[];
  /** Applies a save response's driver/secondSigner outcome immediately — call from the save orchestrator's onSaved. */
  applyFromSaveOutcome: (outcome: SaveOutcomeArguments) => void;
}

/**
 * Wires the BFF's backend validations into RHF field errors for both Signers
 * legs, from both the dashboard GET response and a PUT save's outcome.
 * @param {UseServerFieldErrorsArguments} args - The form instance, current driver/secondSigner sections, and income counts.
 * @returns {UseServerFieldErrorsResult} Unmapped violations per leg (for banner fallback) and the save-outcome applier.
 */
export function useServerFieldErrors({
  form,
  driver,
  secondSigner,
  driverIncomeCount,
  secondSignerIncomeCount,
  driverPhoneCount,
  secondSignerPhoneCount,
}: UseServerFieldErrorsArguments): UseServerFieldErrorsResult {
  const { setError, clearErrors } = form;
  const appliedPathsReference = useRef<Record<"driver" | "secondSigner", Array<FieldPath<SignersInput>>>>({
    driver: [],
    secondSigner: [],
  });
  const [driverUnmapped, setDriverUnmapped] = useState<UnmappedViolation[]>([]);
  const [secondSignerUnmapped, setSecondSignerUnmapped] = useState<UnmappedViolation[]>([]);

  const apply = useCallback(
    (
      personPath: "driver" | "secondSigner",
      validations: ApplicantSectionV2["validations"],
      incomeCount: number,
      phoneCount: number,
      setUnmapped: (unmapped: UnmappedViolation[]) => void
    ) => {
      clearErrors(appliedPathsReference.current[personPath]);
      const { fieldErrors, unmapped } = translatePersonViolations(validations, personPath, {
        incomeCount,
        phoneCount,
      });
      for (const { rhfPath, message } of fieldErrors) {
        setError(rhfPath, { type: "server", message });
      }
      appliedPathsReference.current[personPath] = fieldErrors.map((fieldError) => fieldError.rhfPath);
      setUnmapped(unmapped);
    },
    [setError, clearErrors]
  );

  // Keyed on stringified content (not reference) so a same-shape refetch
  // doesn't reapply, but a genuinely new violations payload always does.
  const driverValidationsKey = JSON.stringify(driver?.validations);
  const secondSignerValidationsKey = JSON.stringify(secondSigner?.validations);

  useEffect(() => {
    apply("driver", driver?.validations, driverIncomeCount, driverPhoneCount, setDriverUnmapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stringified content; `apply`/`driver` themselves are intentionally excluded
  }, [driverValidationsKey, driverIncomeCount, driverPhoneCount]);

  useEffect(() => {
    apply(
      "secondSigner",
      secondSigner?.validations,
      secondSignerIncomeCount,
      secondSignerPhoneCount,
      setSecondSignerUnmapped
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stringified content; `apply`/`secondSigner` themselves are intentionally excluded
  }, [secondSignerValidationsKey, secondSignerIncomeCount, secondSignerPhoneCount]);

  const applyFromSaveOutcome = useCallback(
    (outcome: SaveOutcomeArguments) => {
      // A leg absent from the outcome wasn't part of this save request — its
      // errors (from the last GET, most likely) are left untouched rather
      // than cleared.
      if (outcome.driver) {
        apply("driver", outcome.driver.validations, driverIncomeCount, driverPhoneCount, setDriverUnmapped);
      }
      if (outcome.secondSigner) {
        apply(
          "secondSigner",
          outcome.secondSigner.validations,
          secondSignerIncomeCount,
          secondSignerPhoneCount,
          setSecondSignerUnmapped
        );
      }
    },
    [apply, driverIncomeCount, secondSignerIncomeCount, driverPhoneCount, secondSignerPhoneCount]
  );

  return { driverUnmapped, secondSignerUnmapped, applyFromSaveOutcome };
}
